import { useState } from "react";

import styled from "styled-components";

import { BaseInput } from "components/base";
import FieldErrorMessage from "components/base/FieldErrorMessage";

import { PLACE_JUMP_ZOOM } from "./MapTownshipSelector";
import { JumpActionRow, jumpPanelStyles } from "./jumpPanelLayout";

const initialLatLonValues: Record<string, Nullable<string>> = {
  latitude: null,
  longitude: null
};

// One field per entry, in the order the form asks for them.
const latLonFields = [
  { name: "latitude", placeholder: "Latitude", type: "number", min: "-90", max: "90" },
  {
    name: "longitude",
    placeholder: "Longitude",
    type: "number",
    min: "-180",
    max: "180"
  }
];

const UNABLE_TO_FIND = "Unable to find location";

interface LatLonSelectorProps {
  closeMenu: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  goToPointAndHighlightPolygon: (data: any, zoom?: number) => void;
}

export default function LatLonSelector({
  closeMenu,
  goToPointAndHighlightPolygon
}: LatLonSelectorProps) {
  const [latLonValues, setLatLonValues] = useState(initialLatLonValues);
  const [latLonErrors, setLatLonErrors] = useState("");

  const canSubmit = Object.keys(latLonValues).every((key) => Boolean(latLonValues[key]));

  function handleChange(prop, val) {
    setLatLonErrors("");
    setLatLonValues({ ...latLonValues, [prop]: val });
  }

  function clearLatLonValues() {
    setLatLonErrors("");
    setLatLonValues({ ...initialLatLonValues });
  }

  function onLatLonKeyDown(event) {
    if (event.key === "Enter" && canSubmit) {
      goToLatLon();
    }
  }

  function goToLatLon() {
    setLatLonErrors("");
    try {
      goToPointAndHighlightPolygon(
        {
          Centroid: {
            coordinates: [latLonValues.longitude, latLonValues.latitude]
          }
        },
        PLACE_JUMP_ZOOM
      );
      closeMenu();
    } catch (err) {
      setLatLonErrors(UNABLE_TO_FIND);
    }
  }

  return (
    <LatLonContainer>
      <div className="latlon-fields">
        {latLonFields.map((field) => {
          // A row of filled-in boxes shows nothing but values, so a field that holds
          // one keeps its placeholder as a name above it. The label is the placeholder
          // word again rather than a second copy of it, so the two cannot come apart.
          const filled = Boolean(latLonValues[field.name]);

          return (
            <div
              key={field.name}
              className={filled ? "latlon-field filled" : "latlon-field"}>
              <span className="field-label">{field.placeholder}</span>
              <BaseInput
                placeholder={field.placeholder}
                type={field.type}
                min={field.min}
                max={field.max}
                value={latLonValues[field.name] ?? ""}
                onKeyDown={onLatLonKeyDown}
                onChange={(val) => handleChange(field.name, val)}
              />
            </div>
          );
        })}
      </div>
      <JumpActionRow
        idPrefix="jump-latlon"
        canSubmit={canSubmit}
        onClear={clearLatLonValues}
        onSubmit={goToLatLon}
      />
      {latLonErrors.length > 0 && (
        <div className="flex-row justify-center">
          <FieldErrorMessage message={latLonErrors} />
        </div>
      )}
    </LatLonContainer>
  );
}

const LatLonContainer = styled.div`
  ${jumpPanelStyles}

  .latlon-fields {
    display: flex;
    gap: 8px;
    /* A set that cannot fit at the width its fields are left with takes a second line.
       Nothing here is allowed to push the panel wider than it is and put a sideways
       scrollbar under it. */
    flex-wrap: wrap;
    /* The field row is the first thing in the panel, so the blank line each field keeps for
       its label stands as empty space above the inputs. The row is lifted by exactly that
       one line, which puts the first input at the same height as the other tabs' first
       control. The reserved line stays, so rows do not step down when a label appears. */
    margin-top: calc(-1 * var(--field-label-line));

    .latlon-field {
      /* A field is its name over its own control, so it is the pair and not the input
         that takes a place in the row. The two fields share the row evenly and shrink
         with it, rather than each holding a width of its own. */
      flex: 1 1 0;
      min-width: 72px;
    }
  }
  /* A field is as tall as the ones the other tabs stack. */
  .BaseInput .input {
    height: 32px;
    padding: 6px 8px;
  }
`;
